import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  languageConfig,
  SupportedLanguage,
  useTranslation,
} from '@/hooks/useTranslation';
import { Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export function LanguageSwitcher({
  variant = 'outline',
  size = 'sm',
  className,
}: LanguageSwitcherProps) {
  const { language, changeLanguage } = useTranslation();

  const currentLanguage = languageConfig[language] || languageConfig.en;

  const handleLanguageChange = (lng: SupportedLanguage) => {
    changeLanguage(lng);
    // Update document language for accessibility
    document.documentElement.lang = lng;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          aria-label={`Current language: ${currentLanguage.name}. Click to change language`}
        >
          <Globe className='h-4 w-4' />
          <span className='hidden md:inline ml-2'>
            {currentLanguage.flag} {currentLanguage.nativeName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {Object.entries(languageConfig).map(([code, config]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleLanguageChange(code as SupportedLanguage)}
            className={language === code ? 'bg-accent' : ''}
            aria-label={`Switch to ${config.name}`}
          >
            {config.flag} {config.nativeName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
